// shared-lib/millwork/baseboard.js
/**
 * Where baseboard runs along one wall — the ONE rule the estimate and the 3D
 * view share (claude-docs/2026-10-16-trim-and-paint-design.md §2).
 *
 * Pure.
 */
const {
  FLOOR_LEVEL_TOLERANCE_M,
  isAtFloorLevel,
  blockerStopsBaseboard,
  isFloorLevelOpening,
  isFireplace,
  isStairs,
  isApplianceType,
  BASEBOARD_STOP_FINISHES,
} = require('./blockers');
const { getObjectType } = require('../fixture-types');
const {
  wallLengthM,
  wallStartAxis,
  objAlongWall,
  objAlongExtent,
  objAgainstWall,
} = require('./wallFrame');
const { unionIntervals, subtractIntervals } = require('./intervals');
const { regionRectOnWall, clampPct } = require('./regions');
const { trimStyleOf } = require('./defaults');

/** A stretch shorter than this between two stops is not cut or installed. */
const MIN_SPAN_M = 0.05;

// An object's along-wall interval in the centreline frame, clipped to the wall.
const objectCut = (obj, wall, len) => {
  const a = objAlongWall(obj, wall);
  const half = objAlongExtent(obj, wall) / 2;
  return [Math.max(-len / 2, a - half), Math.min(len / 2, a + half)];
};

// Does a region starting `yPct` up the wall reach the floor? A region at 0 %
// does on any wall; one higher up needs the wall's height to say, and a wall
// without one cannot place it, so it is not taken to.
const regionReachesFloor = (yPct, wallHeight) => {
  if (yPct === 0) return true;
  if (!(wallHeight > 0)) return false;
  return (yPct / 100) * wallHeight <= FLOOR_LEVEL_TOLERANCE_M;
};

// A finish region's along-wall interval in the centreline frame, when it is a
// stopping finish that reaches the floor. Its extent along the wall is
// `regionRectOnWall`'s, the one the take-off bills the tile for.
const regionCut = (region, len, wallHeight, materialTypeOf) => {
  if (!region || region.enabled === false) return null;
  if (!BASEBOARD_STOP_FINISHES.has(materialTypeOf(region))) return null;
  if (!regionReachesFloor(clampPct(region.y ?? 0), wallHeight)) return null;
  const { a0, a1 } = regionRectOnWall(region, len, wallHeight);
  return a1 > a0 ? [a0 - len / 2, a1 - len / 2] : null;
};

// How much of the wall a set of centreline intervals covers: their union,
// clipped to the wall.
const coveredLenM = (intervals, len) =>
  unionIntervals(intervals).reduce(
    (sum, [a, b]) =>
      sum + Math.max(0, Math.min(b, len / 2) - Math.max(a, -len / 2)),
    0
  );

// What a blocking object is, in the words the panels use for what stopped
// the run.
const blockerKind = (obj) => {
  const t = (getObjectType(obj) || '').toLowerCase();
  if (t === 'vanity') return 'vanity';
  if (t === 'bathtub' || t === 'tub') return 'tub';
  if (t === 'shower') return 'shower';
  if (isApplianceType(t)) return 'appliance';
  return 'cabinet';
};

// A stopping finish region's word: slab is stone.
const regionKind = (type) => (type === 'slab' ? 'stone' : type);

/**
 * @param {Object} p
 * @param {Object} p.wall
 * @param {Object[]} p.openings  the hydrated openings on THIS wall
 * @param {Object[]} p.blockers  this wall's entry of `assignWallBlockers`
 * @param {Object[]} p.objects   the room's objects (fireplaces, stairs)
 * @param {number}   p.floorY
 * @param {(region) => string|null} p.materialTypeOf
 * @returns {Object|null} null when the wall's baseboard is not switched on
 */
const baseboardForWall = ({
  wall,
  openings,
  blockers,
  objects,
  floorY,
  materialTypeOf,
}) => {
  if (wall?.trim?.hasBaseboard !== true) return null;
  const axis = wallStartAxis(wall);
  if (!axis) return null;
  const len = wallLengthM(wall);
  const wallHeight = wall?.dimensions?.y;
  const openingCuts = [];
  const stopped = []; // { kind, a0 } beside every cut, for `stops`
  for (const o of openings || []) {
    const ow = o?.dimensions?.x;
    if (typeof ow !== 'number' || !isFloorLevelOpening(o, floorY)) continue;
    const a = objAlongWall(o, wall);
    openingCuts.push([a - ow / 2, a + ow / 2]);
    stopped.push({
      kind: o.category?.door ? 'door' : 'opening',
      a0: a - ow / 2,
    });
  }
  const cuts = [...openingCuts];
  for (const obj of blockers || []) {
    if (!blockerStopsBaseboard(obj, floorY)) continue;
    const cut = objectCut(obj, wall, len);
    cuts.push(cut);
    stopped.push({ kind: blockerKind(obj), a0: cut[0] });
  }
  for (const obj of objects || []) {
    if (!(isFireplace(obj) || isStairs(obj))) continue;
    if (!isAtFloorLevel(obj, floorY) || !objAgainstWall(obj, wall)) continue;
    const cut = objectCut(obj, wall, len);
    cuts.push(cut);
    stopped.push({
      kind: isFireplace(obj) ? 'fireplace' : 'stairs',
      a0: cut[0],
    });
  }
  for (const region of wall?.texture?.regions || []) {
    const cut = regionCut(region, len, wallHeight, materialTypeOf);
    if (!cut) continue;
    cuts.push(cut);
    stopped.push({ kind: regionKind(materialTypeOf(region)), a0: cut[0] });
  }

  const cutsCentreline = unionIntervals(cuts);
  const spans = subtractIntervals([-len / 2, len / 2], cutsCentreline)
    .filter(([s, e]) => e - s >= MIN_SPAN_M)
    .map(([s, e]) => [s + len / 2, e + len / 2]);
  return {
    wallId: wall.id,
    lenM: len,
    axis,
    spans,
    cutsCentreline,
    // the length the floor-level openings take off the wall (their union, on
    // the wall), so `lenM - openingCutLenM - total(spans)` is what the
    // other stops take and is never negative
    openingCutLenM: coveredLenM(openingCuts, len),
    // what stopped the run, one per cut, in wall order
    stops: stopped.sort((x, y) => x.a0 - y.a0).map(({ kind }) => ({ kind })),
    style: trimStyleOf(wall.trim),
    colour: wall.trim.baseboardColor ?? null,
  };
};

module.exports = { baseboardForWall, MIN_SPAN_M };
